import type { Document } from "@models";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useId, useState } from "react";
import { usePatchDocumentMutation } from "src/hooks/mutations";
import { useGetDevices } from "src/hooks/queries";

type SetDocumentScopeDialogProps = {
  document: Document;
  orgId: string;
  onClose: () => void;
};

export const SetDocumentScopeDialog = ({
  document,
  orgId,
  onClose,
}: SetDocumentScopeDialogProps) => {
  const scopeLabelId = useId();
  const {
    data: devices = [],
    isPending: devicesPending,
    isError: devicesError,
  } = useGetDevices({ orgId });
  const mutation = usePatchDocumentMutation();
  const [deviceId, setDeviceId] = useState<string | null>(
    document.deviceId ?? null
  );
  const [error, setError] = useState<string | null>(null);
  const selectedDevice = devices.find((device) => device.id === deviceId);
  const isSaveDisabled =
    mutation.isPending ||
    devicesPending ||
    devicesError ||
    deviceId === (document.deviceId ?? null) ||
    (deviceId !== null && !selectedDevice);
  const close = () => {
    if (!mutation.isPending) onClose();
  };
  const save = async () => {
    if (isSaveDisabled) return;
    setError(null);
    try {
      await mutation.mutateAsync({
        orgId,
        docId: document.id,
        deviceId: document.deviceId ?? "",
        queryDeviceIds: devices.map((device) => device.id),
        data: { deviceId },
      });
      onClose();
    } catch (cause) {
      const response = axios.isAxiosError(cause)
        ? cause.response?.data
        : undefined;
      const message =
        typeof response === "string" ? response : response?.message;
      setError(
        typeof message === "string"
          ? message
          : "Could not set document scope. Please try again."
      );
    }
  };
  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={close}
      aria-labelledby={scopeLabelId}
    >
      <DialogTitle id={scopeLabelId}>Set scope</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className="mb-3">
            {error}
          </Alert>
        )}
        {devicesError && (
          <Alert severity="error">
            Could not load devices. Close this dialog and try again.
          </Alert>
        )}
        {devicesPending ? (
          <CircularProgress aria-label="Loading devices" />
        ) : (
          <div className="flex flex-col gap-4 pt-2">
            <FormControl disabled={mutation.isPending}>
              <FormLabel>Scope</FormLabel>
              <RadioGroup
                aria-label="Scope"
                value={deviceId === null ? "global" : "device"}
                onChange={(_, value) => {
                  setDeviceId(
                    value === "global"
                      ? null
                      : (document.deviceId ?? devices[0]?.id ?? null)
                  );
                  setError(null);
                }}
                className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2"
              >
                {[
                  {
                    value: "global",
                    label: "Global",
                    description: "Applies across the organization",
                  },
                  {
                    value: "device",
                    label: "Specific device",
                    description: "Linked to one device",
                  },
                ].map(({ value, label, description }) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    disabled={
                      mutation.isPending ||
                      (value === "device" && devices.length === 0)
                    }
                    control={<Radio />}
                    className="m-0 rounded-lg border border-solid p-2"
                    sx={{
                      borderColor:
                        (value === "device") === (deviceId !== null)
                          ? "primary.main"
                          : "divider",
                      bgcolor:
                        (value === "device") === (deviceId !== null)
                          ? "action.selected"
                          : "background.paper",
                    }}
                    label={
                      <div>
                        <Typography variant="body2" fontWeight={600}>
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {description}
                        </Typography>
                      </div>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {deviceId !== null && (
              <Autocomplete
                disableClearable
                disabled={mutation.isPending}
                options={devices}
                value={selectedDevice}
                onChange={(_, device) => {
                  setDeviceId(device.id);
                  setError(null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Device" />
                )}
              />
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          variant="outlined"
          disabled={mutation.isPending}
          onClick={close}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaveDisabled}
          onClick={() => void save()}
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
